import { AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import React, { JSX } from "react";

import { AutocompleteActions } from "@components/Search/SearchAutocomplete/AutocompleteSearchBox/useAutocompleteApi.hook";
import ContentPagesTemplate from "@components/Search/SearchAutocomplete/ContentPages/contentPages.template.component";
import ProductsTemplate from "@components/Search/SearchAutocomplete/Products/products.template.component";
import QuerySuggestionsTemplate from "@components/Search/SearchAutocomplete/QuerySuggestions/querySuggestions.template.component";
import RecentSearchesTemplate from "@components/Search/SearchAutocomplete/RecentSearches/recentSearches.template.component";
import {
    CONTENT_PAGES_SOURCE_ID,
    PRODUCTS_SOURCE_ID,
    QUERY_SUGGESTIONS_SOURCE_ID,
    RECENT_SEARCHES_SOURCE_ID,
} from "@components/Search/SearchAutocomplete/autoCompleteConstants";
import {
    ContentPagesSourceType,
    ProductsSourceType,
    QuerySuggestionsSourceType,
    RecentSearchesSourceType,
} from "@components/Search/SearchAutocomplete/autocompleteTypes";

export enum ResultPanelBlock {
    RECENT_SEARCHES = "RECENT_SEARCHES",
    QUERY_SUGGESTIONS = "QUERY_SUGGESTIONS",
    CONTENT_PAGES = "CONTENT_PAGES",
    PRODUCTS = "PRODUCTS",
}

export type TemplateData<TItem extends BaseItem> = {
    items: TItem[];
    autocompleteState?: AutocompleteState<TItem>;
    autocompleteActions?: AutocompleteActions;
};

export type BaseTemplateProps<
    TItem extends BaseItem,
    TOtherProps = {},
> = TemplateData<TItem> & TOtherProps;

export type BaseItemProps<TItem extends BaseItem, TOtherProps = {}> = {
    item: TItem;
    autocompleteState?: AutocompleteState<TItem>;
    autocompleteActions?: AutocompleteActions;
} & TOtherProps;

export type ResultsPanelBlocks = Record<ResultPanelBlock, JSX.Element | null>;

const BlockToSourceMap: Record<ResultPanelBlock, string> = {
    [ResultPanelBlock.RECENT_SEARCHES]: RECENT_SEARCHES_SOURCE_ID,
    [ResultPanelBlock.QUERY_SUGGESTIONS]: QUERY_SUGGESTIONS_SOURCE_ID,
    [ResultPanelBlock.CONTENT_PAGES]: CONTENT_PAGES_SOURCE_ID,
    [ResultPanelBlock.PRODUCTS]: PRODUCTS_SOURCE_ID,
};

export const templates: Record<
    string,
    (templateData: TemplateData<any>) => JSX.Element
> = {
    [PRODUCTS_SOURCE_ID]: (templateData: TemplateData<ProductsSourceType>) => (
        <ProductsTemplate {...templateData} />
    ),
    [CONTENT_PAGES_SOURCE_ID]: (
        templateData: TemplateData<ContentPagesSourceType>,
    ) => <ContentPagesTemplate {...templateData} />,
    [RECENT_SEARCHES_SOURCE_ID]: (
        templateData: TemplateData<RecentSearchesSourceType>,
    ) => <RecentSearchesTemplate {...templateData} />,
    [QUERY_SUGGESTIONS_SOURCE_ID]: (
        templateData: TemplateData<QuerySuggestionsSourceType>,
    ) => <QuerySuggestionsTemplate {...templateData} />,
};

const withTemplate = (
    key: string,
    sources: Record<string, BaseItem[]>,
    autocompleteState?: AutocompleteState<BaseItem>,
    autocompleteActions?: AutocompleteActions,
): JSX.Element | null => {
    if (!sources || !key) return null;

    const template = templates[key];
    const items = sources[key];

    if (!template || !items || items.length === 0) return null;

    return template({
        items: items,
        autocompleteState: autocompleteState,
        autocompleteActions: autocompleteActions,
    });
};

type GetResultPanelBlocks = (
    items: Record<string, BaseItem[]>,
    autocompleteState?: AutocompleteState<BaseItem>,
    autocompleteActions?: AutocompleteActions,
) => ResultsPanelBlocks;
const getResultsPanelBlocks: GetResultPanelBlocks = (
    items,
    autocompleteState,
    autocompleteActions,
) =>
    Object.values(ResultPanelBlock).reduce(
        (blocks, resultsPanelBlock) => ({
            ...blocks,
            [resultsPanelBlock]: withTemplate(
                BlockToSourceMap[resultsPanelBlock],
                items,
                autocompleteState,
                autocompleteActions,
            ),
        }),
        {} as ResultsPanelBlocks,
    );

export { getResultsPanelBlocks };
